/**
 * @file Helpers for storing SAK-related data in local storage.
 */

import { wvusLogger } from "../observability/logging";
import { RequestEletterParams } from "../services/eletterService"; // Import RequestEletterParams type

const SAK_PHOTO_KEY = "sakPhotoData";
const SAK_QUESTIONNAIRE_KEY = "sakQuestionnaireData";
const SAK_SPONSOR_KEY = "sakSponsorData";
const SAK_CHILD_KEY_PREFIX = "sak-"; // Prefix for child-specific completion data

export type SakPhotoData = {
  mediaUrl: string;
  mediaHandshake: string;
};

export type SakQuestionnaireData = {
  topic: string;
  response: string;
};

// same as billingData
export type SakSponsorData = {
  [key: string]: string | undefined;
};

/**
 * Stores SAK photo data in local storage.
 * @param data The photo data to store
 */
export const storeSakPhotoData = (data: SakPhotoData) => {
  try {
    localStorage.setItem(SAK_PHOTO_KEY, JSON.stringify(data));
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const error = new Error(
      "Unable to save SAK photo data to localstorage because: " + errorMessage
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Retrieves SAK photo data from local storage.
 * @returns The stored photo data if found, otherwise null
 */
export const retrieveSakPhotoData = (): SakPhotoData | null => {
  try {
    const data = localStorage.getItem(SAK_PHOTO_KEY);
    return data ? JSON.parse(data) : null;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const error = new Error(
      "Unable to parse SAK photo data from localstorage because: " + errorMessage
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
    return null;
  }
};

/**
 * Removes SAK photo data from local storage.
 */
export const removeSakPhotoData = () => {
  try {
    localStorage.removeItem(SAK_PHOTO_KEY);
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const error = new Error(
      "Unable to remove SAK photo data from localstorage because: " + errorMessage
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Stores SAK questionnaire data in local storage.
 * @param data The questionnaire data to store
 */
export const storeSakQuestionnaireData = (data: SakQuestionnaireData) => {
  try {
    localStorage.setItem(SAK_QUESTIONNAIRE_KEY, JSON.stringify(data));
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const error = new Error(
      "Unable to save SAK questionnaire data to localstorage because: " + errorMessage
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Retrieves SAK questionnaire data from local storage.
 * @returns The stored questionnaire data if found, otherwise null
 */
export const retrieveSakQuestionnaireData = (): SakQuestionnaireData | null => {
  try {
    const data = localStorage.getItem(SAK_QUESTIONNAIRE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const error = new Error(
      "Unable to parse SAK questionnaire data from localstorage because: " + errorMessage
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
    return null;
  }
};

/**
 * Removes SAK questionnaire data from local storage.
 */
export const removeSakQuestionnaireData = () => {
  try {
    localStorage.removeItem(SAK_QUESTIONNAIRE_KEY);
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const error = new Error(
      "Unable to remove SAK questionnaire data from localstorage because: " + errorMessage
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Stores SAK sponsor data in local storage.
 * @param data The sponsor data to store
 */
export const storeSakSponsorData = (data: SakSponsorData) => {
  try {
    localStorage.setItem(SAK_SPONSOR_KEY, JSON.stringify(data));
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const error = new Error(
      "Unable to save SAK sponsor data to localstorage because: " + errorMessage
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Retrieves SAK sponsor data from local storage.
 * @returns The stored sponsor data if found, otherwise null
 */
export const retrieveSakSponsorData = (): SakSponsorData | null => {
  try {
    const data = localStorage.getItem(SAK_SPONSOR_KEY);
    return data ? JSON.parse(data) : null;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const error = new Error(
      "Unable to parse SAK sponsor data from localstorage because: " + errorMessage
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
    return null;
  }
};

/**
 * Removes SAK sponsor data from local storage.
 */
export const removeSakSponsorData = () => {
  try {
    localStorage.removeItem(SAK_SPONSOR_KEY);
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const error = new Error(
      "Unable to remove SAK sponsor data from localstorage because: " + errorMessage
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Stores completed SAK interaction data for a specific child in local storage
 * @param childId The child's ID
 * @param data The data to store
 */
export const storeSakChildCompletion = (childId: string, data: RequestEletterParams) => {
  if (!childId) {
    wvusLogger.log(
      new Error("Cannot store SAK child completion data without a childId"),
      wvusLogger.getLogLevelNames().ERROR
    );
    return;
  }

  try {
    localStorage.setItem(`${SAK_CHILD_KEY_PREFIX}${childId}`, JSON.stringify(data));
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const error = new Error(
      `Unable to save SAK completion data for child ${childId} to localstorage because: ${errorMessage}`
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Retrieves completed SAK interaction data for a specific child from local storage
 * @param childId The child's ID
 * @returns The stored completion data if found, otherwise null
 */
export const retrieveSakChildCompletion = (childId: string): RequestEletterParams | null => {
  if (!childId) {
    wvusLogger.log(
      new Error("Cannot retrieve SAK child completion data without a childId"),
      wvusLogger.getLogLevelNames().ERROR
    );
    return null;
  }

  try {
    const data = localStorage.getItem(`${SAK_CHILD_KEY_PREFIX}${childId}`);
    return data ? JSON.parse(data) : null;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const error = new Error(
      `Unable to parse SAK completion data for child ${childId} from localstorage because: ${errorMessage}`
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
    return null;
  }
};

/**
 * Removes completed SAK interaction data for a specific child from local storage
 * @param childId The child's ID
 */
export const removeSakChildCompletion = (childId: string) => {
  if (!childId) {
    wvusLogger.log(
      new Error("Cannot remove SAK child completion data without a childId"),
      wvusLogger.getLogLevelNames().ERROR
    );
    return;
  }

  try {
    localStorage.removeItem(`${SAK_CHILD_KEY_PREFIX}${childId}`);
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const error = new Error(
      `Unable to remove SAK completion data for child ${childId} from localstorage because: ${errorMessage}`
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};
